import { useTranslation } from 'react-i18next';
import { useGetToolsQuery } from 'app/services/tools';
import { useGetUnitQuery } from 'app/services/me';
import { Tool } from 'app/types/tool';
import { DataTable, Column, ColumnTypes } from 'components/Datatable/DataTable';
import StatusIndicator from '../StatusIndicator';
import { ToolStatusFilter } from 'constants/tool';
import { getToolStatus, matchesToolStatusFilter } from 'utils/tool';

interface TableProps {
  showActive: boolean;
}

const Table = ({ showActive }: TableProps) => {
  const { t } = useTranslation();

  const { data: unit, isLoading: isUnitLoading, isFetching: isUnitFetching } = useGetUnitQuery({});

  const unitId = unit?.Id;

  const { data: tools = [], isLoading, isFetching } = useGetToolsQuery(
    { unitId: unitId || 0, isActive: showActive },
    { skip: !unitId }
  );

  const columns: Column[] = [
    {
      key: 'Label',
      title: t('entities.tools.label'),
      type: ColumnTypes.LINK,
      hasSearch: true,
      hasSort: true,
      link: (tool: Tool) => `/tools/${tool.Id}`,
    },
    {
      key: 'Make',
      title: t('entities.tools.make'),
      type: ColumnTypes.TEXT,
      hasSearch: true,
      hasSort: true,
      defaultOrder: 'ascend',
    },
    {
      key: 'Model',
      title: t('entities.tools.model'),
      type: ColumnTypes.TEXT,
      hasSearch: true,
      hasSort: true,
    },
    {
      key: 'SerialNumber',
      title: t('entities.tools.serialnumber'),
      type: ColumnTypes.TEXT,
      hasSearch: true,
      hasSort: true,
    },
    {
      key: 'Category',
      title: t('entities.tools.category'),
      type: ColumnTypes.TEXT,
      hasSearch: true,
      hasSort: true,
    },
    {
      key: 'QrCode',
      title: t('entities.tools.qrcode'),
      type: ColumnTypes.TEXT,
      hasSearch: true,
      hasSort: true,
    },
    {
      key: 'LastMaintenanceDate',
      title: t('entities.tools.lastmaintenance'),
      type: ColumnTypes.DATE,
      hasSort: true,
      hasDateFilter: true,
    },
    {
      key: 'LastTestDate',
      title: t('entities.tools.lasttest'),
      type: ColumnTypes.DATE,
      hasSort: true,
      hasDateFilter: true,
    },
    {
      key: 'Status',
      title: t('entities.tools.status'),
      type: ColumnTypes.CUSTOM,
      filters: [
        { text: t('entities.tools.statusfilter.maintenanceok'), value: ToolStatusFilter.MaintenanceOk },
        { text: t('entities.tools.statusfilter.maintenancenotok'), value: ToolStatusFilter.MaintenanceNotOk },
        { text: t('entities.tools.statusfilter.testok'), value: ToolStatusFilter.TestOk },
        { text: t('entities.tools.statusfilter.testnotok'), value: ToolStatusFilter.TestNotOk },
      ],
      onFilter: (value, tool: Tool) => matchesToolStatusFilter(tool, value),
      renderAs: (tool: Tool) => {
        const { isMaintenanceOk, isTestOk } = getToolStatus(tool);
        return <StatusIndicator isMaintenanceOk={isMaintenanceOk} isTestOk={isTestOk} />;
      },
    },
  ];

  return (
    <DataTable
      data={tools}
      columns={columns}
      isLoading={isLoading || isUnitLoading || isFetching || isUnitFetching}
      pagination={true}
      tableKey="tools"
    />
  );
};

export default Table;

